import React, { useState, useEffect } from 'react';
import SortableTable from '../../ui/SortableTable';
import { fetchWithAuth } from '../../utils/fetchWithAuth';

const UsersList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        setLoading(true);
        // Using fetchWithAuth to handle authentication headers automatically
        const response = await fetchWithAuth('/users');

        if (!response.ok) {
          throw new Error(`Error: ${response.statusText}`);
        }

        const json = await response.json();

        if (json.success) {
          setUsers(json.data);
        } else {
          throw new Error(json.error || 'Failed to fetch users');
        }
      } catch (err) {
        console.error('Failed to load users:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
  }, []);

  const columns = [
    {
      key: 'username',
      label: 'Nom d\'utilisateur',
      filterable: true,
      sortable: true
    },
    {
      key: 'email',
      label: 'Email',
      filterable: true,
      sortable: true
    },
    {
      key: 'role',
      label: 'Rôle',
      filterable: true,
      sortable: true
    },
    {
      key: 'serviceDeskName',
      label: 'Service Desk',
      filterable: true,
      sortable: true,
      render: (user) => (
        <div className="flex flex-col">
          <span className="font-medium">{user.serviceDeskName}</span>
          <span className="text-xs text-gray-500">ID: {user.serviceDeskId}</span>
        </div>
      ),
      rawValue: (user) => `${user.serviceDeskName} (${user.serviceDeskId})`
    },
    {
      key: 'sectors',
      label: 'Secteurs',
      filterable: true,
      render: (user) => (
        <div className="max-w-xs truncate" title={user.sectors && user.sectors.join(', ')}>
          {user.sectors && user.sectors.length > 0 ? user.sectors.join(', ') : '-'}
        </div>
      ),
      rawValue: (user) => user.sectors ? user.sectors.join(', ') : ''
    },
    {
      key: 'mode',
      label: 'Mode',
      filterable: true,
      sortable: true,
      render: (user) => (
         <span className={`px-2 py-1 rounded-full text-xs ${
           user.mode === 'rw' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
         }`}>
           {user.mode === 'rw' ? 'Lecture/Écriture' : 'Lecture seule'}
         </span>
      ),
      rawValue: (user) => user.mode
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-md">
        <h3 className="font-bold">Une erreur est survenue</h3>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full px-4 py-4">
      <h1 className="text-2xl font-bold mb-4 text-gray-800 flex-shrink-0">Gestion des Utilisateurs</h1>

      <div className="bg-white rounded-lg shadow overflow-hidden flex-1 min-h-0 flex flex-col">
        <SortableTable
          data={users}
          columns={columns}
          emptyMessage="Aucun utilisateur trouvé"
          countLabel={`Total: ${users.length} utilisateurs`}
          initialSortBy={{ key: 'username', direction: 'ascending' }}
          globalSearch={true}
          fullHeight={true}
        />
      </div>
    </div>
  );
};

export default UsersList;

